import React, { useState } from 'react';
import { Button, Dialog, DialogTrigger, DialogContent, Text, Spinner } from '@chatsdet/ui';
import { Clock, Play, Pause, Trash, ArrowsClockwise } from '@phosphor-icons/react';
import { useExploratorySchedules, useCreateExploratorySchedule } from './useExploratoryTests';
import {
  SCHEDULE_PRESETS,
  browserTimezone,
  formatNextRun,
  useParseSchedule,
  useToggleScheduleRule,
  useRunScheduleNow,
  useDeleteScheduleRule,
  ScheduleCadenceFields,
} from '../scheduling';
import type { ExploratoryTestSchedule } from './types';

// Re-export so the existing barrel + preset test keep importing from here.
export { SCHEDULE_PRESETS };

function ScheduleRow({ schedule }: { schedule: ExploratoryTestSchedule }) {
  const toggleMutation = useToggleScheduleRule();
  const runNowMutation = useRunScheduleNow();
  const deleteMutation = useDeleteScheduleRule();

  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-b-0">
      <div className="min-w-0">
        <Text size="sm" weight="medium" className="truncate">
          {schedule.description}
        </Text>
        <Text size="xs" className="text-muted-foreground">
          {schedule.enabled ? `Next run ${formatNextRun(schedule.nextRunAt)}` : 'Paused'}
        </Text>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip={schedule.enabled ? 'Pause' : 'Resume'}
          iconLeft={schedule.enabled ? <Pause size={15} /> : <Play size={15} />}
          onPress={() => toggleMutation.mutate(schedule.ruleId)}
          isPending={toggleMutation.isPending}
        />
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Run now"
          iconLeft={<ArrowsClockwise size={15} />}
          onPress={() => runNowMutation.mutate(schedule.ruleId)}
          isPending={runNowMutation.isPending}
        />
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tone="destructive"
          tooltip="Delete schedule"
          iconLeft={<Trash size={15} />}
          onPress={() => deleteMutation.mutate(schedule.ruleId)}
          isPending={deleteMutation.isPending}
        />
      </div>
    </div>
  );
}

/**
 * Simple modal for scheduling recurring runs of an exploratory test. Authoring
 * lives entirely here (a clock icon → this modal); the automations framework is
 * the backing store. Offers quick presets and an agent textbox that turns a
 * natural-language schedule into a cron + concise English description.
 */
export function ScheduleExploratoryModal({ parentId }: { parentId: string }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<{ cron: string; description: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const schedulesQuery = useExploratorySchedules(open ? parentId : undefined);
  const parseMutation = useParseSchedule();
  const createMutation = useCreateExploratorySchedule();

  const schedules = schedulesQuery.data?.schedules ?? [];

  const create = (cron: string, description: string) => {
    setError(null);
    createMutation.mutate(
      { parentId, cron, timezone: browserTimezone(), description },
      {
        onSuccess: () => {
          setText('');
          setPreview(null);
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'Failed to create schedule'),
      }
    );
  };

  const handleParse = () => {
    if (!text.trim()) return;
    setError(null);
    parseMutation.mutate(text.trim(), {
      onSuccess: (data) => setPreview({ cron: data.cron, description: data.description }),
      onError: (e) =>
        setError(e instanceof Error ? e.message : 'Could not interpret that schedule'),
    });
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen} size="lg">
      <Button
        variant="icon"
        size="sm"
        iconOnly
        tooltip="Schedule runs"
        iconLeft={<Clock size={16} />}
      />
      <Dialog
        title="Schedule runs"
        eyebrow="Exploratory test"
        description="Run this test automatically on a recurring schedule."
      >
        <DialogContent>
          <div className="flex flex-col gap-5">
            <ScheduleCadenceFields
              text={text}
              onTextChange={(v) => {
                setText(v);
                setPreview(null);
              }}
              preview={preview}
              error={error}
              isParsing={parseMutation.isPending}
              isCreating={createMutation.isPending}
              onPreview={handleParse}
              onPresetSelect={(cron, description) => create(cron, description)}
              onCreateFromPreview={() => preview && create(preview.cron, preview.description)}
            />

            {/* Active schedules */}
            <div className="flex flex-col gap-1">
              <Text size="sm" weight="medium" className="mb-1">
                Active schedules
              </Text>
              {schedulesQuery.isLoading ? (
                <div className="py-3">
                  <Spinner size="sm" />
                </div>
              ) : schedules.length === 0 ? (
                <Text size="sm" className="text-muted-foreground">
                  No schedules yet.
                </Text>
              ) : (
                <div>
                  {schedules.map((s) => (
                    <ScheduleRow key={s.ruleId} schedule={s} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DialogTrigger>
  );
}
